import { useMemo, useEffect, useState } from "react";
import cls from "./AdminDashboard.module.scss";
import api from "@/utils/axios";
import { useNavigate } from "react-router-dom";
import { useCall } from "@/app/call-context-state";

import MembersIcon from "@/assets/UI/MembersStat.webp";
import PostsIcon from "@/assets/UI/PostsStats.webp";
import EngagementsIcon from "@/assets/UI/EngagementsStats.webp";
import ConnectionsIcon from "@/assets/UI/ConnectionsStats.webp";
import chatIcon from "@/assets/UI/chat.webp";
import audioCallIcon from "@/assets/UI/audioCall.webp";
import videoCallIcon from "@/assets/UI/videoCall.webp";
import emailIcon from "@/assets/UI/email.webp";
import myProfileIcon from "@/assets/UI/myProfile.webp";

import DefaultAvatar from "@/assets/UI/avatar.png";
import AdminAvatar from "@/assets/UI/admin.webp";

function formatCompactValue(value) {
  if (value < 1000) return String(value);
  const compactValue = (value / 1000).toFixed(1).replace(/\.0$/, "");
  return `${compactValue}k`;
}

function getUserAvatar(user) {
  const username = String(user?.username || "").trim();
  if (username === "NotyAdmin") return AdminAvatar;
  return user?.avatar?.url || DefaultAvatar;
}

function buildMapPositions(count) {
  const presets = [
    { top: "14%", left: "12%" },
    { top: "21%", left: "34%" },
    { top: "16%", left: "58%" },
    { top: "26%", left: "79%" },
    { top: "43%", left: "18%" },
    { top: "49%", left: "40%" },
    { top: "39%", left: "63%" },
    { top: "55%", left: "84%" },
    { top: "70%", left: "14%" },
    { top: "74%", left: "34%" },
    { top: "67%", left: "56%" },
    { top: "79%", left: "78%" },
  ];

  return Array.from({ length: count }, (_, i) => presets[i % presets.length]);
}

function StatCard({ label, value, delta, tone = "mr", icon, iconAlt }) {
  const up = delta >= 0;

  return (
    <article className={`${cls.card} ${cls[`tone-${tone}`]}`}>
      <div className={cls.cardGlow} aria-hidden="true" />

      <div className={cls.cardTop}>
        <div className={cls.cardIconWrap}>
          <div className={cls.cardIcon}>
            <div className={cls.cardIconRing} />
            <div className={cls.cardIconInner}>
              <img src={icon} alt={iconAlt} className={cls.cardIconImage} />
            </div>
          </div>
        </div>

        <div className={cls.cardTrend}>
          <span
            className={`${cls.cardDelta} ${up ? cls.isUp : cls.isDown}`}
            title={`${Math.abs(delta).toFixed(1)}% change`}
          >
            <span className={cls.cardDeltaArrow}>{up ? "↗" : "↘"}</span>
            <span>{Math.abs(delta).toFixed(1)}%</span>
          </span>
        </div>
      </div>

      <div className={cls.cardBody}>
        <p className={cls.cardLabel}>{label}</p>
        <h3 className={cls.cardValue}>{value}</h3>
        <p className={cls.cardHint}>Last 30 days performance</p>
      </div>
    </article>
  );
}

function PresenceNode({ user, index, pos }) {
  const username = user?.username || "Unknown user";
  const avatarSrc = getUserAvatar(user);

  return (
    <div
      className={cls.mapNode}
      style={{
        top: pos.top,
        left: pos.left,
        animationDelay: `${index * 0.12}s`,
      }}
      title={username}
    >
      <button type="button" className={cls.mapNodeButton}>
        <span className={cls.mapNodePulse} />
        <span className={cls.mapNodeRing} />
        <img src={avatarSrc} alt={username} className={cls.mapNodeAvatar} />
      </button>

      <div className={cls.mapNodeLabel}>
        <span className={cls.mapNodeName}>{username}</span>
        <span className={cls.mapNodeStatus}>online</span>
      </div>
    </div>
  );
}

function OnlineUserCard({ user }) {
  const username = user?.username || "Unknown user";
  const avatarSrc = getUserAvatar(user);

  return (
    <article className={cls.onlineCard}>
      <div className={cls.onlineCardGlow} aria-hidden="true" />

      <div className={cls.onlineCardMain}>
        <div className={cls.onlineAvatar}>
          <span className={cls.onlinePulse} />
          <img
            src={avatarSrc}
            alt={`${username} avatar`}
            className={cls.onlineAvatarImage}
          />
        </div>

        <div className={cls.onlineIdentity}>
          <div className={cls.onlineUsername}>{username}</div>
          <div className={cls.onlineStatusRow}>
            <span className={cls.onlineDot} />
            <span className={cls.onlineStatusText}>Live now</span>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { startCall, activeCall, isConnecting } = useCall();
  const [dashboardStats, setDashboardStats] = useState({
    members: { total: 0, deltaLast30Days: 0 },
    posts: { total: 0, deltaLast30Days: 0 },
    engagements: { total: 0, deltaLast30Days: 0 },
    connections: { total: 0, deltaLast30Days: 0 },
  });

  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineLoading, setOnlineLoading] = useState(true);
  const [onlineError, setOnlineError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [memberTypeFilter, setMemberTypeFilter] = useState("all");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const handleStartCall = async (calleeId, callType) => {
    if (!calleeId) return;
    await startCall({ calleeId, callType });
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get("/v1/admin/dashboard/stats");

        setDashboardStats({
          members: {
            total: res.data?.members?.total ?? 0,
            deltaLast30Days: res.data?.members?.deltaLast30Days ?? 0,
          },
          posts: {
            total: res.data?.posts?.total ?? 0,
            deltaLast30Days: res.data?.posts?.deltaLast30Days ?? 0,
          },
          engagements: {
            total: res.data?.engagements?.total ?? 0,
            deltaLast30Days: res.data?.engagements?.deltaLast30Days ?? 0,
          },
          connections: {
            total: res.data?.connections?.total ?? 0,
            deltaLast30Days: res.data?.connections?.deltaLast30Days ?? 0,
          },
        });
      } catch (err) {
        console.error("Dashboard stats fetch failed:", err);
        setDashboardStats({
          members: { total: 0, deltaLast30Days: 0 },
          posts: { total: 0, deltaLast30Days: 0 },
          engagements: { total: 0, deltaLast30Days: 0 },
          connections: { total: 0, deltaLast30Days: 0 },
        });
      }
    };

    const fetchOnlineUsers = async () => {
      try {
        setOnlineError("");
        const res = await api.get("/v1/admin-online/online-users");
        setOnlineUsers(res.data?.users ?? []);
      } catch (err) {
        console.error("Online users fetch failed:", err);
        setOnlineError(
          err?.response?.data?.message || "Failed to load online users.",
        );
        setOnlineUsers([]);
      } finally {
        setOnlineLoading(false);
      }
    };

    fetchStats();
    fetchOnlineUsers();

    const intervalId = setInterval(fetchOnlineUsers, 15000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const trimmed = searchQuery.trim();

    if (!trimmed) {
      setSearchResults([]);
      setSearchError("");
      setSearchLoading(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);
        setSearchError("");

        const res = await api.get("/v1/admin/dashboard/search-members", {
          params: {
            q: trimmed,
            limit: 24,
            ...(memberTypeFilter !== "all"
              ? { memberType: memberTypeFilter }
              : {}),
          },
        });

        setSearchResults(res.data?.items || []);
      } catch (err) {
        console.error("Dashboard search failed:", err);
        setSearchError(
          err?.response?.data?.message || "Failed to search members.",
        );
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 320);

    return () => clearTimeout(timer);
  }, [searchQuery, memberTypeFilter]);

  const stats = useMemo(
    () => [
      {
        label: "Members",
        value: formatCompactValue(dashboardStats.members.total),
        delta: dashboardStats.members.deltaLast30Days,
        tone: "mr",
        icon: MembersIcon,
        iconAlt: "Members icon",
      },
      {
        label: "Posts",
        value: formatCompactValue(dashboardStats.posts.total),
        delta: dashboardStats.posts.deltaLast30Days,
        tone: "mrs",
        icon: PostsIcon,
        iconAlt: "Posts icon",
      },
      {
        label: "Engagements",
        value: formatCompactValue(dashboardStats.engagements.total),
        delta: dashboardStats.engagements.deltaLast30Days,
        tone: "mr",
        icon: EngagementsIcon,
        iconAlt: "Engagements icon",
      },
      {
        label: "Connections",
        value: formatCompactValue(dashboardStats.connections.total),
        delta: dashboardStats.connections.deltaLast30Days,
        tone: "mrs",
        icon: ConnectionsIcon,
        iconAlt: "Connections icon",
      },
    ],
    [dashboardStats],
  );

  const onlineCount = onlineUsers.length;
  const mapPositions = useMemo(
    () => buildMapPositions(Math.max(onlineUsers.length, 12)),
    [onlineUsers.length],
  );

  return (
    <div className={cls.wrap}>
      <section className={cls.heroPanel} aria-label="Dashboard overview">
        <div className={cls.heroGlow} aria-hidden="true" />
        <div className={cls.heroText}>
          <p className={cls.heroEyebrow}>Admin command center</p>
          <h1 className={cls.heroTitle}>Noty Universe live stats</h1>
          <p className={cls.heroSub}>
            Real-time platform activity, member presence, and performance pulse
            view.
          </p>
        </div>

        <div className={cls.heroBadge}>
          <span className={cls.heroBadgeDot} />
          <span>System active</span>
        </div>
      </section>

      <section className={cls.statGrid} aria-label="Key metrics">
        {stats.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </section>

      <section className={cls.searchSection} aria-label="Member search">
        <header className={cls.searchHeader}>
          <div>
            <p className={cls.sectionEyebrow}>User & member modal tools</p>
            <h2 className={cls.searchTitle}>Fast member search</h2>
            <p className={cls.searchSub}>
              Search by username, real name, country, city, and couple/single
              type with quick admin actions.
            </p>
          </div>
        </header>

        <div className={cls.searchControls}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cls.searchInput}
            placeholder="Search username, first name, last name, country or city..."
          />

          <div className={cls.searchFilters}>
            <button
              type="button"
              className={`${cls.filterBtn} ${memberTypeFilter === "all" ? cls.filterBtnActive : ""}`}
              onClick={() => setMemberTypeFilter("all")}
            >
              All
            </button>
            <button
              type="button"
              className={`${cls.filterBtn} ${memberTypeFilter === "single" ? cls.filterBtnActive : ""}`}
              onClick={() => setMemberTypeFilter("single")}
            >
              Single
            </button>
            <button
              type="button"
              className={`${cls.filterBtn} ${memberTypeFilter === "couple" ? cls.filterBtnActive : ""}`}
              onClick={() => setMemberTypeFilter("couple")}
            >
              Couple
            </button>
          </div>
        </div>

        {searchLoading && (
          <div className={cls.searchState}>Searching members...</div>
        )}

        {searchError && !searchLoading && (
          <div className={cls.searchStateError}>{searchError}</div>
        )}

        {!searchLoading &&
          !searchError &&
          searchQuery.trim() &&
          searchResults.length === 0 && (
            <div className={cls.searchState}>
              No users found for this search.
            </div>
          )}

        {!searchLoading && searchResults.length > 0 && (
          <div className={cls.searchGrid}>
            {searchResults.map((user) => {
              const fullName = [user.firstName, user.lastName]
                .filter(Boolean)
                .join(" ");
              const location = [user.country, user.city]
                .filter(Boolean)
                .join(", ");

              return (
                <article
                  key={user.memberId || user.userId}
                  className={cls.searchCard}
                >
                  <div className={cls.searchUserTop}>
                    <img
                      src={getUserAvatar(user)}
                      alt={user.username || "User avatar"}
                      className={cls.searchAvatar}
                    />
                    <div className={cls.searchIdentity}>
                      <div className={cls.searchUsername}>
                        @{user.username || "unknown"}
                      </div>
                      <div className={cls.searchMeta}>
                        {fullName || "No real name"}
                      </div>
                      <div className={cls.searchMeta}>
                        {location || "No location"}
                      </div>
                      <span className={cls.memberTypePill}>
                        {user.memberType === "couple"
                          ? "Couple Member"
                          : "Single Member"}
                      </span>
                    </div>
                  </div>

                  <div className={cls.searchActions}>
                    <button
                      type="button"
                      className={cls.actionBtn}
                      onClick={() => navigate(`/admin/chat/${user.memberId}`)}
                      title="Open admin chat"
                    >
                      <img src={chatIcon} alt="" />
                    </button>
                    <button
                      type="button"
                      className={cls.actionBtn}
                      onClick={() => handleStartCall(user.userId, "audio")}
                      disabled={!user.userId || isConnecting || !!activeCall}
                      title="Start audio call"
                    >
                      <img src={audioCallIcon} alt="" />
                    </button>
                    <button
                      type="button"
                      className={cls.actionBtn}
                      onClick={() => handleStartCall(user.userId, "video")}
                      disabled={!user.userId || isConnecting || !!activeCall}
                      title="Start video call"
                    >
                      <img src={videoCallIcon} alt="" />
                    </button>
                    <button
                      type="button"
                      className={cls.actionBtn}
                      onClick={() =>
                        navigate(`/admin/members/email/${user.userId}`)
                      }
                      title="Open email page"
                    >
                      <img src={emailIcon} alt="" />
                    </button>
                    <button
                      type="button"
                      className={cls.actionBtn}
                      onClick={() =>
                        navigate(`/admin/members/single/${user.userId}`)
                      }
                      title="Open member profile"
                    >
                      <img src={myProfileIcon} alt="" />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className={cls.onlineSection} aria-label="Online users">
        <div className={cls.onlineSectionGlow} aria-hidden="true" />

        <header className={cls.onlineHeader}>
          <div className={cls.onlineTitleWrap}>
            <p className={cls.sectionEyebrow}>Presence monitor</p>
            <h2 className={cls.onlineTitle}>Live member constellation</h2>
            <p className={cls.onlineSubtitle}>
              A futuristic presence map of users currently active in Noty
              Universe
            </p>
          </div>

          <div className={cls.onlineMeta}>
            <div className={cls.onlineCountBadge}>{onlineCount}</div>
            <div className={cls.onlineCountText}>
              <span className={cls.onlineCountValue}>
                {onlineCount === 1
                  ? "1 member online"
                  : `${onlineCount} members online`}
              </span>
              <span className={cls.onlineCountHint}>Real-time pulse</span>
            </div>
          </div>
        </header>

        {onlineLoading && (
          <div className={cls.statePanel}>
            <span className={cls.stateOrb} />
            <p className={cls.onlineInfo}>Scanning live presence...</p>
          </div>
        )}

        {onlineError && !onlineLoading && (
          <div className={`${cls.statePanel} ${cls.stateError}`}>
            <span className={cls.stateOrb} />
            <p className={cls.onlineError}>{onlineError}</p>
          </div>
        )}

        {!onlineLoading && !onlineError && onlineCount === 0 && (
          <div className={cls.statePanel}>
            <span className={cls.stateOrb} />
            <p className={cls.onlineInfo}>
              No active members detected right now.
            </p>
          </div>
        )}

        {!onlineLoading && !onlineError && onlineCount > 0 && (
          <>
            <div className={cls.mapBoard}>
              <div className={cls.mapBackdrop} />
              <div className={cls.mapGrid} />
              <div className={cls.mapOrbA} />
              <div className={cls.mapOrbB} />
              <div className={cls.mapCore} />

              <div className={cls.mapBrand} aria-hidden="true">
                <span className={cls.mapBrandTop}>NOTY</span>
                <span className={cls.mapBrandBottom}>UNIVERSE</span>
              </div>

              {onlineUsers.map((user, index) => (
                <PresenceNode
                  key={user._id || `${user.username}-${index}`}
                  user={user}
                  index={index}
                  pos={mapPositions[index]}
                />
              ))}
            </div>

            <div className={cls.onlineGrid}>
              {onlineUsers.map((u) => (
                <OnlineUserCard key={u._id || u.username} user={u} />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
